import config from 'config';

import { handleResponse } from 'utils/Api/common';

const {
  constants: {
    API: { PS_CORE_BASE_URL },
  },
} = config;

/**
 * Investor Profile modal data — ATAI-109 (profile snapshot) and ATAI-169
 * (owned/sold properties), both behind propstream-core's gateway.
 *
 * Plain fetches rather than the CALL_API middleware, same reasoning as
 * data/contacts/psCoreService.js: these are one-off, per-record reads for a
 * single open modal instance, and the middleware's request queue (dedupes by
 * method+path+query, aborts an older in-flight duplicate without running
 * either callback) is easy to hang this kind of UI on.
 */
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'x-auth-token': window.appAuthToken,
});

const investorUrl = (investorId, resource = '') =>
  `${PS_CORE_BASE_URL}/ps-core/investors/${investorId}${resource}`;

// GET /ps-core/investors/{investorId} — the stored profile snapshot (ATAI-109).
// No live vendor call on the service side; 404 when the investor was never
// materialized by a prior search.
export const fetchInvestorProfile = async (investorId) => {
  const response = await fetch(investorUrl(investorId), {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

// GET /ps-core/investors/{investorId}/saved-groups — the lightweight sibling
// of fetchInvestorProfile: just this investor's current Investor List
// memberships, each with the contactId the Remove flow needs (ATAI-186).
// Deliberately its own endpoint rather than reusing the full profile: a
// search-result card only needs to know what's actually saved *right now*,
// not the whole portfolio snapshot, and unlike the S2 search response (which
// is fetched once and never self-updates) this always reflects the latest
// membership — call it fresh on every Remove click rather than trusting
// `investor.savedGroups` off the card, which goes stale the moment any list
// membership changes (e.g. removing one of several lists from an investor
// still saved elsewhere).
export const fetchInvestorSavedGroups = async (investorId) => {
  const response = await fetch(investorUrl(investorId, '/saved-groups'), {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

// GET /ps-core/investors/{investorId}/properties?ownership=current|previous —
// up to 10 PropStream-enriched rows plus totalCount (ATAI-169). `current` =
// Acquisition (owned), `previous` = Disposition (sold).
export const fetchInvestorProperties = async (investorId, ownership) => {
  const response = await fetch(investorUrl(investorId, `/properties?ownership=${ownership}`), {
    method: 'GET',
    headers: authHeaders(),
  });

  return handleResponse(response);
};

// POST /ps-core/investors/save-snapshot — persists the investor object as the
// search returned it (ATAI-113 / S7), keyed by its BatchData id. A full
// replace, stamped "now" by the server, which drops any contacts sent with it
// (ATAI-182 — the snapshot is shared across users; phone/email are per-user
// data in propstream-be).
//
// Called on Add to Investor List (AddToInvestorListModal) and on Search's
// "View Details" (useInvestorProfileData — see its header for why that one
// stays). Send the full search-result object: since ATAI-172 it carries the
// whole metric set, so it never thins out a stored snapshot. Never send the
// thin `{id, contactId}` object the Contacts Investor List opens with.
// POST /ps-core/investors/resolve-contacts — for a batch of BatchData investor
// ids, the Investor Lists each one is saved to, every entry carrying the
// `contactId` of the materialized contact (ATAI-117).
//
// The actions downstream of a save (Skip Trace, Dialer, Email) all key on
// contact ids, which a search result does not have until propstream-be's
// materialize creates one. This is how a selection gets from investor ids to
// contact ids in a single request rather than one per investor.
//
// Investors with no contact are simply ABSENT from the map — the same
// convention the search response's savedGroups uses, so there is no separate
// "not found" list to interpret.
export const resolveInvestorContacts = async (investorIds) => {
  const response = await fetch(`${PS_CORE_BASE_URL}/ps-core/investors/resolve-contacts`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ investorIds }),
  });

  return handleResponse(response);
};

// GET /ps-core/investors/{investorId}/properties/export — the investor's FULL
// currently-owned property list as a CSV file (S12).
//
// A plain link can't be used: the endpoint needs the x-auth-token header, which
// a browser navigation won't send. So fetch it, then hand the bytes to a
// throwaway object URL to trigger the save. The service pins the body as
// UTF-8-with-BOM (so Excel reads accented names correctly), which survives
// because the blob is built from the raw bytes and never decoded to a string.
//
// Owned only — the endpoint takes no ownership parameter and the sold list is
// not exported.
export const exportInvestorPropertiesCsv = async (investorId) => {
  const response = await fetch(investorUrl(investorId, '/properties/export'), {
    method: 'GET',
    headers: { 'x-auth-token': window.appAuthToken },
  });

  if (!response.ok) {
    throw new Error(`Export failed with ${response.status}`);
  }

  // Prefer the filename the service chose, so the file is named the same way
  // however it was requested; fall back if the header is absent (or stripped by
  // a proxy that doesn't expose it to JS).
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match
    ? decodeURIComponent(match[1])
    : `investor-${investorId}-owned-properties.csv`;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const saveInvestorSnapshot = async (investorId, investor) => {
  const response = await fetch(`${PS_CORE_BASE_URL}/ps-core/investors/save-snapshot`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      snapshots: [{ batchdataInvestorId: investorId, investor }],
    }),
  });

  return handleResponse(response);
};

// Same endpoint, one call for many -- InvestorSnapshotService.saveSnapshots
// already loops over the whole `snapshots` array in one transaction, so a
// multi-investor save (e.g. "Add to Investor List" on several search
// results) doesn't need one request per investor.
export const saveInvestorSnapshots = async (investors) => {
  const response = await fetch(`${PS_CORE_BASE_URL}/ps-core/investors/save-snapshot`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      snapshots: investors.map((investor) => ({
        batchdataInvestorId: investor.id,
        investor,
      })),
    }),
  });

  return handleResponse(response);
};

// ATAI-114 / S8: Investor Status & Touchpoint moved to data/contacts/
// psCoreService.js — they're keyed by the contact's own id, not the
// BatchData investor id every function on this file uses, so they belong
// alongside Notes/Documents (same contactUrl helper, same
// ContactAccessService-backed endpoints, just reached under the
// `/investors` URL prefix instead of `/contacts` — both alias the same
// controller/data).
